"use client";

import { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete"; // ← NEW: Searchable dropdown
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Chip from "@mui/material/Chip";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import BackupTableIcon from "@mui/icons-material/BackupTable";
import Typography from "@mui/material/Typography";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { createRoute, updateRoute, deleteRoute, deleteAllRoutes } from "../../api/routes";
import customFetch from "hooks/fetchWrapper";
import ConfirmationDialog from "hooks/ConfirmationDialog";
import Menu from "@mui/material/Menu";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import * as XLSX from "xlsx";
import { Select } from "@mui/material";

const RouteTab = () => {
  const [rows, setRows] = useState([]);
  const [areas, setAreas] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [formData, setFormData] = useState({
    rid: null,
    routename: "",
    routeno: "",
    map: "",
    EmployeeCode: "", // Selected driver's EmployeeCode
    selectedDriver: null, // Full driver object for Autocomplete
    areas: [],
  });

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [excelMenuAnchorEl, setExcelMenuAnchorEl] = useState(null);
  const fileInputRef = useRef(null);

  const [openDeleteAllDialog, setOpenDeleteAllDialog] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [canConfirm, setCanConfirm] = useState(false);
  const countdownRef = useRef(null);

  useEffect(() => {
    fetchRoutes();
    fetchAreas();
    fetchDrivers();
  }, []);

  useEffect(() => {
    if (openDeleteAllDialog && countdown > 0) {
      countdownRef.current = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0) {
      setCanConfirm(true);
      clearTimeout(countdownRef.current);
    }

    return () => clearTimeout(countdownRef.current);
  }, [openDeleteAllDialog, countdown]);

  const handleCloseDeleteAll = () => {
    setOpenDeleteAllDialog(false);
    setCountdown(5);
    setCanConfirm(false);
    clearTimeout(countdownRef.current);
  };

  const handleConfirmDeleteAll = async () => {
    try {
      const response = await deleteAllRoutes();
      if (response.status) {
        setRows([]); // Clear the table immediately
        alert("All routes deleted successfully!");
      } else {
        alert("Failed to delete all routes");
      }
    } catch (error) {
      console.error("Delete all routes failed:", error);
      alert("Operation failed");
    } finally {
      handleCloseDeleteAll();
    }
  };

  const fetchRoutes = async () => {
    try {
      const data = await customFetch("routes");
      if (data.status && Array.isArray(data.data)) setRows(data.data);
    } catch (error) {
      console.error("Error fetching routes:", error);
    }
  };

  const fetchAreas = async () => {
    try {
      const data = await customFetch("areas");
      if (data.status && Array.isArray(data.data)) setAreas(data.data);
    } catch (error) {
      console.error("Error fetching areas:", error);
    }
  };

  const fetchDrivers = async () => {
    try {
      const data = await customFetch("getDriverDetails/0");
      if (Array.isArray(data)) setDrivers(data);
    } catch (error) {
      console.error("Error fetching drivers:", error);
    }
  };

  const handleOpenAddModal = () => {
    setIsEditMode(false);
    setFormData({
      rid: null,
      routename: "",
      routeno: "",
      map: "",
      EmployeeCode: "",
      selectedDriver: null,
      areas: [],
    });
    setOpenModal(true);
  };

  const handleOpenEditModal = (route) => {
    const currentDriver =
      drivers.find((d) => d.EmployeeCode === route.driver?.EmployeeCode) || null;

    setIsEditMode(true);
    setFormData({
      rid: route.rid,
      routename: route.routename || "",
      routeno: route.routeno || "",
      map: route.map || "",
      EmployeeCode: route.driver?.EmployeeCode || "",
      selectedDriver: currentDriver,
      areas: route.areas?.map((a) => a.aid) || [],
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleSubmit = async () => {
    if (!formData.routename || !formData.routeno || formData.areas.length === 0) {
      alert("Please fill required fields");
      return;
    }

    const areaNames = formData.areas
      .map((aid) => areas.find((a) => a.aid === aid)?.areaname || "")
      .filter(Boolean)
      .join(",");

    const payload = [
      {
        ...(isEditMode && { rid: formData.rid }),
        routename: formData.routename,
        routeno: formData.routeno,
        map: formData.map || "",
        DriverID: formData.EmployeeCode || null, // Send EmployeeCode as DriverID
        BusNumber: Number(formData.routeno),
        areas: areaNames,
      },
    ];

    try {
      const response = isEditMode ? await updateRoute(payload) : await createRoute(payload);
      if (response.status) {
        fetchRoutes();
        handleCloseModal();
        alert(isEditMode ? "Route updated!" : "Route added!");
      }
    } catch (error) {
      console.error("Operation failed:", error);
      alert("Operation failed");
    }
  };

  const handleDelete = (id) => () => {
    setDeleteId(id);
    setOpenConfirmDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteRoute(deleteId);
      setRows((prev) => prev.filter((r) => r.rid !== deleteId));
    } catch (error) {
      console.error(error);
    }
    setOpenConfirmDialog(false);
  };

  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const headers = [["routename", "routeno", "area", "map_url", "DriverID"]];
    // const example = [
    //   ["AL FURJAN", "32", "Garden,Al Furjan", "https://map.example.com/r101", "IQ01"],
    // ];
    const ws = XLSX.utils.aoa_to_sheet([...headers]);
    XLSX.utils.book_append_sheet(wb, ws, "Routes");
    XLSX.writeFile(wb, `routes_template_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet);

        const payload = json
          .map((row) => ({
            routename: String(row.routename || "").trim(),
            routeno: String(row.routeno || "").trim(),
            areas: String(row.area || "").trim(),
            map: String(row.map_url || "").trim(),
            DriverID: String(row.DriverID || "").trim(),
            BusNumber: Number(row.routeno || 0),
          }))
          .filter((r) => r.routename && r.routeno && r.areas && r.DriverID);

        if (payload.length === 0) return alert("No valid data");

        const res = await createRoute(payload);
        if (res.status) {
          fetchRoutes();
          alert(`Imported ${payload.length} routes!`);
        }
      } catch (err) {
        alert("Invalid Excel");
      }
      e.target.value = null;
    };
    reader.readAsArrayBuffer(file);
  };

  const columns = [
    { field: "routename", headerName: "Route Name", width: 200 },
    { field: "routeno", headerName: "Route No", width: 100 },
    {
      field: "areas",
      headerName: "Areas",
      width: 300,
      valueGetter: (p) => p.row.areas?.map((a) => a.areaname).join(", ") || "",
    },
    {
      field: "emirate",
      headerName: "Emirate",
      width: 130,
      valueGetter: (p) =>
        [...new Set(p.row.areas?.map((a) => a.emirateName || ""))].filter(Boolean).join(", ") ||
        "—",
    },
    {
      field: "map",
      headerName: "Map URL",
      width: 150,
      renderCell: (p) =>
        p.value ? (
          <a href={p.value} target="_blank" rel="noreferrer">
            View
          </a>
        ) : (
          "—"
        ),
    },
    {
      field: "driver",
      headerName: "Driver",
      width: 200,
      valueGetter: (p) => p.row.driver?.DriverName || "Not Assigned",
    },
    {
      field: "actions",
      type: "actions",
      width: 100,
      getActions: (p) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleOpenEditModal(p.row)}
        />,
        <GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={handleDelete(p.id)} />,
      ],
    },
  ];

  return (
    <Box sx={{ height: 700, width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 2 }}>
        <Button
          variant="contained"
          color="error"
          onClick={() => {
            setOpenDeleteAllDialog(true);
            setCountdown(5);
            setCanConfirm(false);
          }}
        >
          Delete All Routes
        </Button>
        <Button
          variant="contained"
          startIcon={<BackupTableIcon />}
          onClick={(e) => setExcelMenuAnchorEl(e.currentTarget)}
        >
          Excel
        </Button>
        <Menu
          anchorEl={excelMenuAnchorEl}
          open={Boolean(excelMenuAnchorEl)}
          onClose={() => setExcelMenuAnchorEl(null)}
        >
          <MenuItem onClick={handleDownloadTemplate}>
            <ListItemIcon>
              <FileDownloadIcon />
            </ListItemIcon>
            <ListItemText>Download Template</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => fileInputRef.current?.click()}>
            <ListItemIcon>
              <FileUploadIcon />
            </ListItemIcon>
            <ListItemText>Upload Excel</ListItemText>
          </MenuItem>
        </Menu>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".xlsx,.xls"
          style={{ display: "none" }}
        />
        <Button variant="contained" onClick={handleOpenAddModal}>
          Add New Route
        </Button>
      </Box>

      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(r) => r.rid}
        pageSizeOptions={[10, 25, 50]}
      />

      {/* Modal */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            width: { xs: "90%", sm: 600 },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <h2>{isEditMode ? "Edit Route" : "Add New Route"}</h2>

          <TextField
            label="Route Name"
            fullWidth
            margin="dense"
            value={formData.routename}
            onChange={(e) => setFormData({ ...formData, routename: e.target.value })}
          />
          <TextField
            label="Route No (Bus No)"
            fullWidth
            margin="dense"
            value={formData.routeno}
            onChange={(e) => setFormData({ ...formData, routeno: e.target.value })}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Select Areas</InputLabel>
            <Select
              multiple
              value={formData.areas}
              onChange={(e) => setFormData({ ...formData, areas: e.target.value })}
              input={<OutlinedInput label="Select Areas" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((aid) => {
                    const a = areas.find((x) => x.aid === aid);
                    return <Chip key={aid} label={a?.areaname} size="small" />;
                  })}
                </Box>
              )}
            >
              {areas.map((a) => (
                <MenuItem key={a.aid} value={a.aid}>
                  {a.code} - {a.areaname} ({a.emirateName})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Map URL (optional)"
            fullWidth
            margin="dense"
            value={formData.map}
            onChange={(e) => setFormData({ ...formData, map: e.target.value })}
          />

          {/* SEARCHABLE DRIVER DROPDOWN */}
          <Autocomplete
            options={drivers}
            getOptionLabel={(option) => `${option.DriverName} (${option.EmployeeCode})`}
            value={formData.selectedDriver}
            onChange={(event, newValue) => {
              setFormData({
                ...formData,
                selectedDriver: newValue,
                EmployeeCode: newValue?.EmployeeCode || "",
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Driver (Search by Name or Code)"
                margin="dense"
                placeholder="Type to search driver..."
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.DriverID}>
                <Box>
                  <strong>{option.DriverName}</strong> ({option.EmployeeCode})
                </Box>
              </li>
            )}
            fullWidth
            clearOnEscape
            sx={{ mt: 2 }}
          />

          <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {isEditMode ? "Update" : "Create"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={openDeleteAllDialog} onClose={handleCloseDeleteAll}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Delete ALL Routes?
          </Typography>
          <Typography variant="body1" sx={{ my: 3 }}>
            This will permanently delete all routes and cannot be undone.
          </Typography>
          <Typography variant="h4" sx={{ my: 4, fontWeight: "bold" }}>
            {countdown}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {canConfirm
              ? "You can now confirm deletion"
              : "Please wait for the countdown to finish"}
          </Typography>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "center", gap: 2 }}>
            <Button onClick={handleCloseDeleteAll} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDeleteAll}
              disabled={!canConfirm}
            >
              Yes, Delete All
            </Button>
          </Box>
        </Box>
      </Modal>

      <ConfirmationDialog
        open={openConfirmDialog}
        title="Delete Route"
        description="Are you sure?"
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={confirmDelete}
      />
    </Box>
  );
};

export default RouteTab;
